import React, { useState } from 'react';
import { Button, ButtonGroup, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import {
    FaPlus,
    FaMoneyBillWave,
    FaWallet,
    FaExchangeAlt,
    FaArrowDown,
    FaArrowUp,
    FaArrowsAltH
} from 'react-icons/fa';
import './transactionButtons.css';

export const TransactionButtons = ({ onSelectTransactionType, showModal }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const transactionTypes = [
        {
            type: 'pendapatan',
            label: 'Pemasukan',
            icon: FaArrowDown,
            color: 'success',
            description: 'Catat uang masuk',
            gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
        },
        {
            type: 'pengeluaran',
            label: 'Pengeluaran',
            icon: FaArrowUp,
            color: 'danger',
            description: 'Catat uang keluar',
            gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
        },
        {
            type: 'transfer',
            label: 'Transfer',
            icon: FaArrowsAltH,
            color: 'info',
            description: 'Transfer antar akun',
            gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
        }
    ];

    const handleButtonClick = (transactionType) => {
        setShowMenu(false);
        if (onSelectTransactionType) {
            onSelectTransactionType(transactionType);
        }
        if (showModal) {
            showModal(transactionType);
        }
    };

    const MainButton = () => (
        <OverlayTrigger
            placement="top"
            show={showTooltip}
            overlay={<Tooltip>Catat Transaksi Baru</Tooltip>}
        >
            <Button
                className="main-floating-btn"
                onClick={() => setShowMenu(!showMenu)}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
            >
                <FaPlus className="main-icon" />
            </Button>
        </OverlayTrigger>
    );

    const ActionButton = ({ item, index }) => (
        <OverlayTrigger
            placement="left"
            overlay={<Tooltip>{item.description}</Tooltip>}
        >
            <Button
                className={`action-btn ${item.type}-btn`}
                style={{ background: item.gradient }}
                onClick={() => handleButtonClick(item.type)}
                data-type={item.type}
            >
                <item.icon className="action-icon" />
                <span className="action-label">{item.label}</span>
            </Button>
        </OverlayTrigger>
    );

    return (
        <div className="transaction-buttons-container">
            {/* Menu Buttons */}
            <div className={`action-buttons ${showMenu ? 'show' : ''}`}>
                {transactionTypes.map((item, index) => (
                    <div 
                        key={item.type} 
                        className="action-button-wrapper"
                        style={{ 
                            animationDelay: `${index * 0.1}s`,
                            transform: `translateY(${showMenu ? -((index + 1) * 70) : 0}px)`
                        }}
                    >
                        <ActionButton item={item} index={index} />
                    </div>
                ))}
            </div>

            {/* Main Floating Button */}
            <MainButton />

            {/* Backdrop */}
            {showMenu && (
                <div 
                    className="menu-backdrop"
                    onClick={() => setShowMenu(false)}
                />
            )}
        </div>
    );
};

// Alternative Version - Horizontal Button Group
export const HorizontalTransactionButtons = ({ onSelectTransactionType, compact = false }) => {
    const transactionTypes = [
        {
            type: 'pendapatan',
            label: 'Pemasukan',
            icon: FaArrowDown,
            color: 'outline-success',
            shortLabel: 'Masuk'
        },
        {
            type: 'pengeluaran',
            label: 'Pengeluaran',
            icon: FaArrowUp,
            color: 'outline-danger',
            shortLabel: 'Keluar'
        },
        {
            type: 'transfer',
            label: 'Transfer',
            icon: FaArrowsAltH,
            color: 'outline-info',
            shortLabel: 'Transfer'
        }
    ];

    return (
        <div className={`horizontal-buttons ${compact ? 'compact' : ''}`}>
            <ButtonGroup className="transaction-button-group">
                {transactionTypes.map((item) => (
                    <Button
                        key={item.type}
                        variant={item.color}
                        className="transaction-type-btn"
                        onClick={() => onSelectTransactionType(item.type)}
                    >
                        <item.icon className="btn-icon" />
                        {!compact && <span>{item.label}</span>}
                        {compact && <span>{item.shortLabel}</span>}
                    </Button>
                ))}
            </ButtonGroup>
        </div>
    );
};

// Modal Trigger Version
export const ModalTransactionButtons = ({ onButtonClick }) => {
    const [showModal, setShowModal] = useState(false);

    const transactionOptions = [
        {
            type: 'pendapatan',
            title: 'Catat Pemasukan',
            icon: FaMoneyBillWave,
            description: 'Tambahkan pemasukan baru ke akun Anda',
            buttonText: 'Pemasukan',
            variant: 'success'
        },
        {
            type: 'pengeluaran',
            title: 'Catat Pengeluaran',
            icon: FaWallet,
            description: 'Catat pengeluaran dari akun Anda',
            buttonText: 'Pengeluaran',
            variant: 'danger'
        },
        {
            type: 'transfer',
            title: 'Transfer Dana',
            icon: FaExchangeAlt,
            description: 'Transfer dana antar akun',
            buttonText: 'Transfer',
            variant: 'info'
        }
    ];

    const handleSelect = (type) => {
        setShowModal(false);
        if (onButtonClick) {
            onButtonClick(type);
        }
    };

    return (
        <>
            <Button
                variant="primary"
                className="modal-trigger-btn"
                onClick={() => setShowModal(true)}
            >
                <FaPlus className="me-2" />
                Transaksi
            </Button>

            <Modal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                centered
                className="transaction-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaPlus className="me-2" />
                        Jenis Transaksi
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="transaction-options">
                        {transactionOptions.map((option) => (
                            <div 
                                key={option.type}
                                className="transaction-option-card"
                                onClick={() => handleSelect(option.type)}
                            >
                                <div className={`option-icon ${option.variant}`}>
                                    <option.icon />
                                </div>
                                <div className="option-content">
                                    <h5>{option.title}</h5>
                                    <p>{option.description}</p>
                                </div>
                                <Button variant={option.variant} size="sm">
                                    Pilih
                                </Button>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default TransactionButtons;